import type { QueryRankingToolConfigProvider } from '@empathyco/playboard-core-domain'
import type { ComputedRef, Ref } from 'vue'
import type { EpbSelectOption } from '../components/shared/epb-select'
import type { FieldValueSelectValue } from '../components/shared/field-value-select.types'
import { computed, ref } from 'vue'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'

export interface BrowseListFilterComposable {
  browseFilterValue: Ref<FieldValueSelectValue>
  browseFieldOptions: ComputedRef<Array<EpbSelectOption>>
  browseExtraParams: ComputedRef<Record<string, string>>
  setBrowseFilter: (value: FieldValueSelectValue) => void
  clearBrowseFilter: () => void
}

const EMPTY_BROWSE_FILTER: FieldValueSelectValue = { name: undefined, value: undefined }
const EMPTY_BROWSE_EXTRA_PARAMS: Record<string, string> = {}

const browseFilterValue = ref<FieldValueSelectValue>({ ...EMPTY_BROWSE_FILTER })

export function useBrowseListFilter(): BrowseListFilterComposable {
  const getQueryRankingToolConfig = CONTAINER.get<QueryRankingToolConfigProvider>(
    TYPES.QueryRankingToolConfigProvider,
  )

  const browseFieldOptions = computed<Array<EpbSelectOption>>(
    () =>
      getQueryRankingToolConfig().browseFields?.map(({ id, displayName, isCategory }) => ({
        value: id,
        label: displayName ?? id,
        isCategory: isCategory ?? false,
      })) ?? [],
  )

  const browseExtraParams = computed<Record<string, string>>(() => {
    const { name, value } = browseFilterValue.value
    if (!name || !value) {
      return EMPTY_BROWSE_EXTRA_PARAMS
    }
    return { browseField: name, browseValue: value }
  })

  function setBrowseFilter({ name, value }: FieldValueSelectValue): void {
    browseFilterValue.value = { name, value }
  }

  function clearBrowseFilter(): void {
    browseFilterValue.value = { ...EMPTY_BROWSE_FILTER }
  }

  return {
    browseFilterValue,
    browseFieldOptions,
    browseExtraParams,
    setBrowseFilter,
    clearBrowseFilter,
  }
}
